<template>
<div class="mi-root" :style="themePageStyle">

  <!-- Topbar -->
  <nav class="mi-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Market Intelligence</span>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-warning d-none d-md-inline-block" @click="$router.push('/dashboard')"><i class="mdi mdi-view-dashboard me-1"></i>Dashboard</button>
      <button class="btn btn-sm btn-warning" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i>Marketplace</button>
    </div>
  </div></nav>

  <div class="container mi-container">
    <!-- Hero -->
    <div class="text-center mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">📈 Livestock Market Prices</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;max-width:640px;margin:0 auto;">
        Weekly average prices per species and county, sourced from Flockr transactions and verified market surveys.
        Use the filters to narrow down — click any row to see the trend.
      </p>
    </div>

    <!-- Filters -->
    <div class="mi-filters mb-4">
      <div class="row g-2">
        <div class="col-md-4">
          <label class="mi-lbl">Species</label>
          <select v-model="filter.speciesId" class="form-select mi-input" @change="loadPrices">
            <option value="">All species</option>
            <option v-for="s in species" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="mi-lbl">County</label>
          <select v-model="filter.countyId" class="form-select mi-input" @change="loadPrices">
            <option value="">All counties</option>
            <option v-for="c in counties" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="mi-lbl">Period</label>
          <select v-model="filter.periodType" class="form-select mi-input" @change="loadPrices">
            <option value="">Any</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="daily">Daily</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Prices grid -->
    <div v-if="loadingPrices" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!prices.length" class="text-center py-5">
      <i class="mdi mdi-chart-line-variant" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No price data yet</h5>
      <p style="color:var(--fm-s,#6B6356);">Check back soon — admin publishes prices weekly.</p>
    </div>
    <div v-else class="row g-3">
      <div class="col-md-6 col-lg-4" v-for="p in prices" :key="p.id">
        <div class="mi-card" :class="{active: trendKey === p.species_id+'-'+p.county_id+'-'+(p.breed_id||0)}" @click="loadTrend(p)">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div class="mi-spec">{{ p.species_name }}<span v-if="p.breed_name"> · {{ p.breed_name }}</span></div>
              <div class="mi-county"><i class="mdi mdi-map-marker me-1"></i>{{ p.county_name }}</div>
            </div>
            <span class="mi-period">{{ p.period_type }}</span>
          </div>
          <div class="mi-avg">KES {{ fNum(p.avg_price) }}<span class="mi-unit">/{{ p.price_unit === 'per_kg' ? 'kg' : 'animal' }}</span></div>
          <div class="mi-range" v-if="p.min_price || p.max_price">
            <span><i class="mdi mdi-arrow-down me-1" style="color:#FF6B6B;"></i>KES {{ fNum(p.min_price) }}</span>
            <span><i class="mdi mdi-arrow-up me-1" style="color:#50C878;"></i>KES {{ fNum(p.max_price) }}</span>
          </div>
          <div class="mi-meta">
            <span><i class="mdi mdi-calendar me-1"></i>{{ p.record_date }}</span>
            <span v-if="p.sample_size > 0">· {{ p.sample_size }} txns</span>
            <span v-if="p.total_volume" >· {{ p.total_volume }} animals</span>
          </div>
          <div class="mi-source">{{ humanizeSource(p.source) }}</div>
        </div>
      </div>
    </div>

    <!-- Trend chart (lazy-loaded after click) -->
    <div v-if="trendKey" class="mi-trend-card mt-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 style="color:var(--fm-h,#F5F0E8);margin:0;">
          <i class="mdi mdi-chart-line me-1" style="color:#BFA164;"></i>
          {{ trendLabel }}
        </h6>
        <button class="btn btn-sm btn-link" style="color:var(--fm-s,#6B6356);text-decoration:none;font-size:11px;" @click="trendKey=''; trendSeries=[]">
          <i class="mdi mdi-close"></i> Close
        </button>
      </div>
      <div v-if="loadingTrend" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
      <div v-else-if="!trendSeries.length" class="text-center py-3" style="color:var(--fm-s,#6B6356);font-size:12px;">No historical data for this combination yet.</div>
      <div v-else>
        <!-- Inline SVG sparkline + axis labels -->
        <svg :viewBox="'0 0 '+chartW+' '+chartH" :width="'100%'" :height="chartH" preserveAspectRatio="none" class="mi-chart">
          <polyline :points="chartLine" fill="none" stroke="#BFA164" stroke-width="2.5" stroke-linejoin="round" />
          <polyline :points="chartArea" fill="rgba(191,161,100,.12)" stroke="none" />
          <circle v-for="(p,i) in chartPoints" :key="i" :cx="p.x" :cy="p.y" r="3" fill="#BFA164" />
        </svg>
        <div class="d-flex justify-content-between" style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:6px;">
          <span>{{ trendSeries[0].record_date }}</span>
          <span>{{ trendSeries[trendSeries.length-1].record_date }}</span>
        </div>
        <div class="d-flex justify-content-between" style="font-size:11px;color:var(--fm-m,#A09888);margin-top:4px;">
          <span>Min: <strong style="color:#FF6B6B;">KES {{ fNum(trendStats.min) }}</strong></span>
          <span>Avg: <strong style="color:#BFA164;">KES {{ fNum(trendStats.avg) }}</strong></span>
          <span>Max: <strong style="color:#50C878;">KES {{ fNum(trendStats.max) }}</strong></span>
          <span>Change: <strong :style="{color: trendStats.changePct >= 0 ? '#50C878' : '#FF6B6B'}">{{ trendStats.changePct >= 0 ? '+' : '' }}{{ trendStats.changePct.toFixed(1) }}%</strong></span>
        </div>
      </div>
    </div>

    <!-- Reports -->
    <div class="mt-5">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 style="color:var(--fm-h,#F5F0E8);margin:0;"><i class="mdi mdi-file-chart me-2" style="color:#BFA164;"></i>Market Reports</h5>
        <span style="color:var(--fm-s,#6B6356);font-size:11px;">{{ reports.length }} report{{ reports.length===1?'':'s' }}</span>
      </div>
      <div v-if="loadingReports" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
      <div v-else-if="!reports.length" class="text-center py-4" style="color:var(--fm-s,#6B6356);font-size:13px;">
        No reports published yet.
      </div>
      <div v-else class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="r in reports" :key="r.id">
          <div class="mi-report" @click="openReport(r)">
            <div class="mi-report-cover" :style="r.cover_image_url ? ('background-image:url('+imgUrl(r.cover_image_url)+')') : ''">
              <i v-if="!r.cover_image_url" class="mdi mdi-file-chart"></i>
              <span class="mi-report-type">{{ r.report_type }}</span>
            </div>
            <div class="mi-report-body">
              <div class="mi-report-title">{{ r.title }}</div>
              <div class="mi-report-meta">
                <span><i class="mdi mdi-calendar me-1"></i>{{ r.report_date }}</span>
                <span v-if="r.county_name">· {{ r.county_name }}</span>
                <span v-if="r.species_name">· {{ r.species_name }}</span>
              </div>
              <div class="mi-report-excerpt">{{ r.excerpt }}…</div>
              <div class="mi-report-stats">
                <span><i class="mdi mdi-eye-outline me-1"></i>{{ r.view_count }}</span>
                <span v-if="r.download_count"><i class="mdi mdi-download me-1"></i>{{ r.download_count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .mi-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .mi-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .mi-card,
html[data-theme="light"] .mi-trend-card,
html[data-theme="light"] .mi-report{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .mi-input{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
</style>

<style scoped>
.mi-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.mi-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.mi-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.mi-filters{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:14px 16px}
.mi-lbl{color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;letter-spacing:.5px;font-weight:600;display:block;margin-bottom:4px}
.mi-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.mi-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}

.mi-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:16px;cursor:pointer;transition:all .2s;height:100%}
.mi-card:hover{border-color:#BFA164;transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.2)}
.mi-card.active{border-color:#BFA164;background:rgba(191,161,100,.05)}
.mi-spec{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px}
.mi-county{color:var(--fm-m,#A09888);font-size:11px}
.mi-period{font-size:10px;padding:2px 8px;border-radius:6px;background:rgba(191,161,100,.1);color:#BFA164;text-transform:capitalize}
.mi-avg{color:#BFA164;font-weight:700;font-size:24px;margin:6px 0 2px}
.mi-unit{color:var(--fm-s,#6B6356);font-size:11px;font-weight:400;margin-left:4px}
.mi-range{display:flex;gap:14px;font-size:11px;color:var(--fm-m,#A09888);margin-bottom:8px}
.mi-meta{font-size:10px;color:var(--fm-s,#6B6356);display:flex;gap:6px;flex-wrap:wrap}
.mi-source{font-size:9px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.5px;margin-top:6px;padding-top:6px;border-top:1px dashed var(--fm-br,#1E1E1E)}

.mi-trend-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:18px}
.mi-chart{width:100%;display:block}

.mi-report{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;overflow:hidden;cursor:pointer;transition:all .2s;height:100%;display:flex;flex-direction:column}
.mi-report:hover{border-color:#BFA164;transform:translateY(-2px)}
.mi-report-cover{height:120px;background:var(--fm-bg2,#0F0F0F);display:flex;align-items:center;justify-content:center;font-size:40px;color:var(--fm-s,#3A3530);position:relative;background-size:cover;background-position:center}
.mi-report-type{position:absolute;top:8px;right:8px;background:rgba(0,0,0,.6);color:#FFD700;font-size:10px;padding:2px 8px;border-radius:6px;text-transform:uppercase;letter-spacing:.4px}
.mi-report-body{padding:14px}
.mi-report-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px;line-height:1.4;margin-bottom:6px}
.mi-report-meta{color:var(--fm-s,#6B6356);font-size:10px;margin-bottom:8px}
.mi-report-excerpt{color:var(--fm-m,#A09888);font-size:11px;line-height:1.5;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.mi-report-stats{display:flex;gap:10px;font-size:10px;color:var(--fm-s,#6B6356);margin-top:8px;padding-top:8px;border-top:1px dashed var(--fm-br,#1E1E1E)}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";

export default {
  name: "MarketIntelligence",
  data(){return{
    darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
    themeObserver: null,
    species: [], counties: [],
    filter: { speciesId:'', countyId:'', periodType:'' },
    prices: [], loadingPrices: false,
    reports: [], loadingReports: false,
    trendKey: '', trendLabel: '', trendSeries: [], loadingTrend: false,
    chartW: 600, chartH: 140
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    chartPoints(){
      // Map series to SVG coords. Linear x, padded y.
      var series = this.trendSeries; if (!series.length) return [];
      var minP = Math.min.apply(null, series.map(function(s){return Number(s.avg_price);}));
      var maxP = Math.max.apply(null, series.map(function(s){return Number(s.avg_price);}));
      if (maxP === minP) maxP = minP + 1;
      var padTop = 12, padBot = 22, padX = 12;
      var w = this.chartW, h = this.chartH;
      var stepX = (w - padX*2) / Math.max(1, series.length - 1);
      var self = this;
      return series.map(function(s, i){
        var t = (Number(s.avg_price) - minP) / (maxP - minP);
        return { x: padX + stepX * i, y: padTop + (1 - t) * (h - padTop - padBot) };
      });
    },
    chartLine(){ return this.chartPoints.map(function(p){return p.x.toFixed(1)+','+p.y.toFixed(1);}).join(' '); },
    chartArea(){
      var pts = this.chartPoints; if (!pts.length) return '';
      var bot = (this.chartH - 12).toFixed(1);
      var s = pts[0].x.toFixed(1) + ',' + bot + ' ';
      s += pts.map(function(p){return p.x.toFixed(1)+','+p.y.toFixed(1);}).join(' ');
      s += ' ' + pts[pts.length-1].x.toFixed(1) + ',' + bot;
      return s;
    },
    trendStats(){
      var s = this.trendSeries; if (!s.length) return { min:0, max:0, avg:0, changePct:0 };
      var prices = s.map(function(x){return Number(x.avg_price);});
      var min = Math.min.apply(null, prices);
      var max = Math.max.apply(null, prices);
      var avg = Math.round(prices.reduce(function(a,b){return a+b;}, 0) / prices.length);
      var first = prices[0], last = prices[prices.length-1];
      var changePct = first > 0 ? ((last - first) / first) * 100 : 0;
      return { min: min, max: max, avg: avg, changePct: changePct };
    }
  },
  mounted(){
    this._applyThemeOnce();
    var self = this;
    this.themeObserver = new MutationObserver(function(){ try { self._applyThemeOnce(); } catch(e) {} });
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    this.loadReferenceData();
    this.loadPrices();
    this.loadReports();
  },
  beforeUnmount(){
    if (this.themeObserver) { this.themeObserver.disconnect(); this.themeObserver = null; }
  },
  methods:{
    _applyThemeOnce(){
      var current = document.documentElement.getAttribute('data-theme');
      var desired = (localStorage.getItem('flockr_theme') || current || 'dark');
      if (current !== desired) document.documentElement.setAttribute('data-theme', desired);
      this.darkMode = desired !== 'light';
    },
    fNum(n){ return Number(n||0).toLocaleString(); },
    imgUrl(u){ if(!u) return ''; if(u.indexOf('http')===0) return u; return uploads + String(u).replace(/^uploads\//, ''); },
    humanizeSource(s){
      var map = { platform_transactions:'Platform transactions', market_survey:'Market survey', government_data:'Government data', aggregated:'Aggregated' };
      return map[s] || s;
    },
    async loadReferenceData(){
      // Pull species + counties for the dropdowns. Both endpoints are public.
      try {
        var sp = await executeJson({ function:'getAnimalSpecies' }, flocker);
        if (sp.data && sp.data.success) this.species = (sp.data.data && sp.data.data.species) || sp.data.data || [];
        var co = await executeJson({ function:'getCounties' }, flocker);
        if (co.data && co.data.success) this.counties = (co.data.data && co.data.data.counties) || co.data.data || [];
      } catch(e){}
    },
    async loadPrices(){
      this.loadingPrices = true;
      try {
        var params = { function:'getMarketPrices' };
        if (this.filter.speciesId) params.speciesId = this.filter.speciesId;
        if (this.filter.countyId)  params.countyId  = this.filter.countyId;
        if (this.filter.periodType) params.periodType = this.filter.periodType;
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success) this.prices = (r.data.data && r.data.data.prices) || [];
      } catch(e){ console.error(e); }
      this.loadingPrices = false;
    },
    async loadReports(){
      this.loadingReports = true;
      try {
        var r = await executeJson({ function:'getMarketReports' }, flocker);
        if (r.data && r.data.success) this.reports = (r.data.data && r.data.data.reports) || [];
      } catch(e){ console.error(e); }
      this.loadingReports = false;
    },
    async loadTrend(p){
      var key = p.species_id + '-' + p.county_id + '-' + (p.breed_id||0);
      // Click again to collapse
      if (this.trendKey === key) { this.trendKey = ''; this.trendSeries = []; return; }
      this.trendKey = key;
      this.trendLabel = p.species_name + (p.breed_name ? ' · ' + p.breed_name : '') + ' in ' + p.county_name;
      this.loadingTrend = true; this.trendSeries = [];
      try {
        var params = { function:'getMarketPriceTrend', speciesId: p.species_id, countyId: p.county_id, months: 12 };
        if (p.breed_id) params.breedId = p.breed_id;
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success) this.trendSeries = (r.data.data && r.data.data.series) || [];
      } catch(e){ console.error(e); }
      this.loadingTrend = false;
    },
    openReport(r){
      this.$router.push('/market/report/' + (r.slug || r.id));
    }
  }
};
</script>
